import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, Reorder } from "framer-motion";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sparkles, Trash2, Plus, Save, ArrowLeft, Zap, GripVertical } from "lucide-react";
import { toast } from "sonner";

const TRIGGER_TYPES = [
  { value: "manual", label: "Manuel (bouton)" },
  { value: "schedule", label: "Programmé (cron)" },
  { value: "gmail_new_email", label: "Nouvel email Gmail" },
  { value: "slack_message", label: "Message Slack" },
  { value: "webhook", label: "Webhook" },
];

const STEP_TYPES = [
  { value: "gmail_send", label: "Envoyer un email" },
  { value: "slack_send", label: "Envoyer message Slack" },
  { value: "notion_create_page", label: "Créer page Notion" },
  { value: "ai_process", label: "Traiter avec IA" },
  { value: "delay", label: "Attendre (délai)" },
  { value: "condition", label: "Condition" },
];

const emptyWorkflow = {
  name: "",
  description: "",
  trigger: { type: "manual", config: {} },
  steps: [],
  estimated_time_saved_minutes: 15,
  active: true,
};

export default function WorkflowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wf, setWf] = useState(emptyWorkflow);
  const [aiDesc, setAiDesc] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/workflows/${id}`).then((r) => setWf(r.data)).catch(() => toast.error("Workflow introuvable"));
    }
  }, [id]);

  const generateWithAI = async () => {
    if (!aiDesc.trim()) return toast.error("Décris ton processus.");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-workflow", { description: aiDesc });
      setWf({ ...emptyWorkflow, ...data, active: true });
      toast.success("Workflow généré !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec génération");
    } finally {
      setGenerating(false);
    }
  };

  const addStep = () => {
    setWf((w) => ({
      ...w,
      steps: [...w.steps, { id: `step-${w.steps.length + 1}`, type: "slack_send", name: "Nouvelle étape", config: {} }],
    }));
  };

  const removeStep = (idx) => setWf((w) => ({ ...w, steps: w.steps.filter((_, i) => i !== idx) }));
  const updateStep = (idx, patch) => setWf((w) => ({
    ...w,
    steps: w.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
  }));

  const save = async () => {
    if (!wf.name.trim()) return toast.error("Un nom est requis.");
    setSaving(true);
    try {
      if (id) {
        await api.put(`/workflows/${id}`, wf);
        toast.success("Workflow mis à jour");
      } else {
        const { data } = await api.post("/workflows", wf);
        toast.success("Workflow créé");
        navigate(`/app/workflows/${data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="workflow-editor-page">
      <Link to="/app/workflows" className="flex items-center gap-2 text-sm text-zinc-500 hover:text-black mb-6">
        <ArrowLeft size={14}/> Retour aux workflows
      </Link>

      <div className="mb-8">
        <span className="overline">{id ? "Édition" : "Création"}</span>
        <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">
          {id ? "Modifier le workflow" : "Nouveau workflow"}
        </h1>
      </div>

      {/* AI generation */}
      {!id && (
        <div className="bg-black text-white rounded-sm p-8 mb-8 grain" data-testid="ai-generator-card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18}/>
            <h2 className="font-display text-xl font-medium tracking-tight">Génération IA</h2>
            <Badge className="klein-bg text-white rounded-sm font-mono text-[10px] ml-2">Claude Sonnet 4.5</Badge>
          </div>
          <p className="text-zinc-400 mb-4 text-sm">Décris ton processus en langage naturel. L'IA construit le workflow.</p>
          <Textarea
            value={aiDesc}
            onChange={(e) => setAiDesc(e.target.value)}
            rows={3}
            placeholder="Ex: Chaque lundi à 9h, envoie un récap des nouveaux leads de la semaine à l'équipe commerciale sur Slack."
            className="rounded-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 font-mono text-sm"
            data-testid="ai-description-input"
          />
          <Button
            onClick={generateWithAI}
            disabled={generating}
            className="mt-4 klein-bg text-white hover:bg-blue-700 rounded-sm"
            data-testid="ai-generate-btn"
          >
            {generating ? "Génération..." : (<><Zap size={14} className="mr-2"/> Générer avec l'IA</>)}
          </Button>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-6 space-y-5">
        <div>
          <Label htmlFor="name">Nom du workflow</Label>
          <Input id="name" value={wf.name} onChange={(e) => setWf({ ...wf, name: e.target.value })} className="rounded-sm mt-2" placeholder="Ex: Tri des emails facturation" data-testid="workflow-name-input"/>
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={wf.description || ""} onChange={(e) => setWf({ ...wf, description: e.target.value })} className="rounded-sm mt-2" placeholder="Que fait ce workflow ?" data-testid="workflow-description-input"/>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Temps économisé / exécution (min)</Label>
            <Input type="number" value={wf.estimated_time_saved_minutes} onChange={(e) => setWf({ ...wf, estimated_time_saved_minutes: parseInt(e.target.value) || 0 })} className="rounded-sm mt-2" data-testid="workflow-time-saved-input"/>
          </div>
          <div>
            <Label>État</Label>
            <Select value={wf.active ? "active" : "draft"} onValueChange={(v) => setWf({ ...wf, active: v === "active" })}>
              <SelectTrigger className="rounded-sm mt-2" data-testid="workflow-state-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Trigger */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-6">
        <span className="overline">Déclencheur</span>
        <h3 className="font-display text-xl font-medium tracking-tight mt-2 mb-4">Quand ce workflow s'exécute-t-il ?</h3>
        <Select value={wf.trigger.type} onValueChange={(v) => setWf({ ...wf, trigger: { type: v, config: {} } })}>
          <SelectTrigger className="rounded-sm max-w-md" data-testid="trigger-type-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            {TRIGGER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Steps */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="overline">Étapes</span>
            <h3 className="font-display text-xl font-medium tracking-tight mt-2">Actions à exécuter</h3>
          </div>
          <Button onClick={addStep} variant="outline" className="rounded-sm" data-testid="add-step-btn">
            <Plus size={14} className="mr-1"/> Ajouter
          </Button>
        </div>

        {wf.steps.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-6">Aucune étape. Utilise l'IA ou ajoute-les manuellement.</p>
        ) : (
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-[26px] top-6 bottom-6 w-px bg-zinc-200 pointer-events-none" aria-hidden="true"/>
            <Reorder.Group
              axis="y"
              values={wf.steps}
              onReorder={(newSteps) => setWf((w) => ({ ...w, steps: newSteps }))}
              className="space-y-3 relative"
            >
              {wf.steps.map((step, idx) => (
                <Reorder.Item
                  key={step.id || idx}
                  value={step}
                  className="relative border border-zinc-200 rounded-sm p-4 bg-white cursor-move shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`step-row-${idx}`}
                  whileDrag={{ scale: 1.02, zIndex: 10, boxShadow: "0 12px 30px -10px rgba(0,0,0,0.15)" }}
                >
                  <div className="flex items-start gap-3">
                    <GripVertical size={16} className="text-zinc-300 mt-2 shrink-0" aria-label="drag"/>
                    <div className="w-9 h-9 rounded-sm bg-black text-white flex items-center justify-center font-mono text-xs shrink-0 mt-0.5 relative z-10">
                      {idx + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input value={step.name} onChange={(e) => updateStep(idx, { name: e.target.value })} className="rounded-sm" placeholder="Nom de l'étape"/>
                      <Select value={step.type} onValueChange={(v) => updateStep(idx, { type: v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {STEP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" onClick={() => removeStep(idx)} className="rounded-sm border-zinc-300 text-red-600 hover:bg-red-50 shrink-0">
                      <Trash2 size={14}/>
                    </Button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            <p className="text-[11px] text-zinc-400 mt-3 flex items-center gap-1.5">
              <GripVertical size={11}/> Glissez-déposez pour réordonner les étapes
            </p>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center justify-end gap-3">
        <Link to="/app/workflows">
          <Button variant="outline" className="rounded-sm">Annuler</Button>
        </Link>
        <Button onClick={save} disabled={saving} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="workflow-save-btn">
          <Save size={14} className="mr-2"/> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
